// @flow
/* eslint-env browser */
import React, { Component, Fragment, createRef, type ElementRef, type Node } from 'react';
import classnames from 'classnames';
import { isEmpty } from 'lodash';
import { FocusOn } from 'react-focus-on';

import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { TrackClick } from 'Tracking/track-click/TrackClick.jsx';
import { BodyLocker } from 'Components/frameworks/body-locker/BodyLocker.jsx';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink.jsx';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import { Languages, type LanguagesProps } from 'Components/widgets/languages/Languages.jsx';
import { GlobalSearch } from 'Components/widgets/global-search/GlobalSearch.jsx';
import { NavigationMemberAccessButton } from 'Components/widgets/navigation/member-access/member-access-button/NavigationMemberAccessButton.jsx';
import { NavigationMemberAccess } from 'Components/widgets/navigation/member-access/NavigationMemberAccess.jsx';
import {
  elements as ELEMENTS,
  heights as HEIGHTS,
  NAVIGATION_OFFSET_SELECTOR,
} from 'Components/widgets/navigation/Navigation.constants';
import {
  NAVIGATION_TRACKING_LOGO_BUTTON,
  NAVIGATION_TRACKING_CONTACT_BUTTON,
  getNavigationLanguagesButtonTrackingData,
} from 'Components/widgets/navigation/Navigation.tracking';
import { NavigationContact } from 'Components/widgets/navigation/contact/NavigationContact.jsx';

import { NavigationLargeBackPanel } from './panels/NavigationLargeBackPanel.jsx';
import { NavigationLargeElement } from './panels/NavigationLargeElement.jsx';
import { NavigationLargePanel } from './panels/NavigationLargePanel.jsx';
import { NavigationLargeFirstLevelItem } from './first-level-item/NavigationLargeFirstLevelItem.jsx';
import { NavigationLargeSubNav } from './subnavs/NavigationLargeSubNav.jsx';
import { NavigationLargeSubNavList, type NavItem } from './subnavs/NavigationLargeSubNavList.jsx';

export const POPHOVER_IN_DELAY = 300;
export const POPHOVER_OUT_DELAY = 500;

type Props = {
  backToB2CWebsiteLabel: string,
  backToB2CWebsiteLink: string,
  cmsAccount: CMSAccount,
  cmsMemberSpace: CMSMemberSpace,
  contact: CMSNavigationBarContact,
  id?: string,
  isMicePage: boolean,
  items: Array<NavItem>,
  language: LanguagesProps,
  MICELogoRedirectionLink: string,
  promoBannerRef: { current: ElementRef<Node> | null },
  userInfo?: UserInfo,
};

type State = {
  activeElement: string | null,
  activeIndex: number,
  height: *,
};

export class NavigationLarge extends Component<Props, State> {
  static defaultProps = {
    id: 'Navigation',
    language: {},
  };
  navigationNode: *;
  hoverInTimeout: TimeoutID;
  hoverOutTimeout: TimeoutID;

  constructor(props: Props) {
    super(props);

    this.navigationNode = createRef();

    this.state = {
      activeElement: null,
      activeIndex: -1,
      height: 0,
    };
  }

  componentDidMount() {
    if (typeof window !== 'undefined') {
      document.addEventListener('click', this.onClickOutside);
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      document.removeEventListener('click', this.onClickOutside);
    }
    clearTimeout(this.hoverInTimeout);
    clearTimeout(this.hoverOutTimeout);
  }

  onClickOutside = (e: Event) => {
    if (this.state.activeElement) {
      if (!this.navigationNode.current || !this.navigationNode.current.contains(e.target)) {
        this.setState({ activeElement: null });
      }
    }
  };

  setSubnav = (activeIndex: number, isFocus?: boolean) => () => {
    let height = 0;
    if (typeof window !== 'undefined') {
      // $FlowFixMe
      height = this[`subnav${activeIndex}`] ? this[`subnav${activeIndex}`].clientHeight : 0;
    }

    clearTimeout(this.hoverOutTimeout);
    this.hoverInTimeout = setTimeout(() => {
      this.setState({
        activeElement: null,
        activeIndex,
        height,
      });
    }, isFocus ? 0 : POPHOVER_IN_DELAY);
  };

  resetSubnav = (isFocus?: boolean) => () => {
    clearTimeout(this.hoverInTimeout);
    this.hoverOutTimeout = setTimeout(() => {
      this.setState({
        activeIndex: -1,
        height: 0,
      });
    }, isFocus ? 0 : POPHOVER_OUT_DELAY);
  };

  toggleElement = (activeElement: ?string) => () => {
    this.setState(prevState => ({
      activeElement: prevState.activeElement === activeElement ? null : activeElement,
      activeIndex: -1,
      height: 0,
    }));
  };

  render() {
    const {
      id,
      contact,
      items,
      language,
      promoBannerRef,
      userInfo,
      isMicePage,
      backToB2CWebsiteLabel,
      backToB2CWebsiteLink,
      MICELogoRedirectionLink,
    } = this.props;

    const { isLoggedIn, isQuickLoggedIn } = userInfo || {};

    const { activeElement } = this.state;

    const iconButtonClassNames =
      'reset-button cursor-pointer flex items-center px-4 relative focus:text-blue hover:text-blue';

    const offsetTop =
      promoBannerRef && promoBannerRef.current ? promoBannerRef.current.offsetHeight : 0;

    const isGlobalSearchActiveElement = activeElement === ELEMENTS.SEARCH;

    const hasContactDetails =
      contact?.hours || contact?.description || contact?.toll || contact?.title;

    const logoTitle = isMicePage ? 'Club Med meetings and events page' : 'Club Med Homepage';

    return (
      <nav
        aria-label="ClubMed main navigation"
        className={`${NAVIGATION_OFFSET_SELECTOR} fadeInLong absolute bg-white top-0 inset-x-0 z-5`}
        id={id}
      >
        <FocusOn allowPinchZoom enabled={isGlobalSearchActiveElement}>
          <BodyLocker isLocked={isGlobalSearchActiveElement}>
            <NavigationLargeBackPanel height={this.state.height} />
            <div
              className="border-b-1 border-gray-light flex items-stretch justify-between px-5"
              ref={this.navigationNode}
              style={{
                height: HEIGHTS.NAVIGATION_LARGE_HEIGHT,
              }}
            >
              <TrackClick
                as={DynamicLink}
                className="cursor-pointer flex flex-shrink-0 items-center px-5 text-blue"
                data-testid="NavigationLarge-logo"
                onFocus={this.resetSubnav(true)}
                queryParamsToIgnore={['selectedProposalId', 'proposalIds', 'optionIds']}
                title={logoTitle}
                trackingData={NAVIGATION_TRACKING_LOGO_BUTTON}
                url={`${isMicePage ? MICELogoRedirectionLink : '/'}`}
              >
                <Icon
                  className={classnames({ '-mt-1': !isMicePage, 'mt-1': isMicePage })}
                  isNotSquare
                  name={isMicePage ? ICONS.CLUBMED_MEETINGS : ICONS.CLUBMED}
                  width="10rem"
                />
                <span className="a11y">{logoTitle}</span>
              </TrackClick>
              <ul className="reset-list flex" style={{ flex: '0 1 auto' }}>
                {items.map((item, index) => {
                  const subItems = item.subItems || [];
                  const hasSubItems = !!subItems.length;
                  const { isList, label } = item;
                  const isActive = index === this.state.activeIndex;

                  const subnavName = `subnav${index}`;

                  return (
                    !!label && (
                      <li
                        className="flex items-stretch"
                        data-testid="NavigationLarge-item"
                        key={label}
                        onFocus={this.setSubnav(index, true)}
                        onMouseEnter={this.setSubnav(index)}
                        onBlur={this.resetSubnav(true)}
                        onMouseLeave={this.resetSubnav()}
                      >
                        <NavigationLargeFirstLevelItem
                          color={item.color}
                          hasSubItems={hasSubItems}
                          isActive={isActive}
                          link={item.link}
                        >
                          {label}
                        </NavigationLargeFirstLevelItem>
                        {hasSubItems && (
                          <NavigationLargePanel
                            isActive={isActive}
                            // $FlowFixMe
                            ref={elem => (this[subnavName] = elem)}
                          >
                            {isList ? (
                              <NavigationLargeSubNavList parentLabel={label} subItems={subItems} />
                            ) : (
                              <NavigationLargeSubNav subItems={subItems} />
                            )}
                          </NavigationLargePanel>
                        )}
                      </li>
                    )
                  );
                })}
              </ul>
              <div className="flex flex-shrink-0 relative">
                {!isEmpty(contact) && (
                  <Fragment>
                    <TrackClick
                      as="button"
                      aria-expanded={activeElement === ELEMENTS.CONTACT}
                      data-testid="NavigationLarge-contact-button"
                      className={classnames(iconButtonClassNames, 'navigation-arrow', {
                        'text-blue navigation-arrow-active': activeElement === ELEMENTS.CONTACT,
                      })}
                      onClick={hasContactDetails ? this.toggleElement(ELEMENTS.CONTACT) : undefined}
                      style={{ fontSize: '0.8125rem' }}
                      title={contact.title}
                      trackingData={
                        activeElement === ELEMENTS.CONTACT || !hasContactDetails
                          ? null
                          : NAVIGATION_TRACKING_CONTACT_BUTTON
                      }
                      type="button"
                    >
                      <Icon
                        className="rtl:mr-1"
                        color={COLORS.BLUE}
                        hasRtl
                        name={ICONS.PHONE}
                        width="1em"
                      />
                      <span className="a11y">
                        {contact.title}
                        &nbsp;
                      </span>
                      <span className="font-bold text-blue whitespace-no-wrap" dir="ltr">
                        {contact.phoneNumber}
                      </span>
                    </TrackClick>
                    <NavigationLargeElement
                      onClose={this.toggleElement(ELEMENTS.CONTACT)}
                      style={{ width: '18.75rem' }}
                    >
                      {activeElement === ELEMENTS.CONTACT ? (
                        <NavigationContact contact={contact} />
                      ) : null}
                    </NavigationLargeElement>
                  </Fragment>
                )}

                {isMicePage && (
                  <TrackClick
                    as={DynamicLink}
                    trackingData={{
                      categoryGA: 'headerMenu',
                      actionGA: 'header_backToB2C',
                      labelGA: 'backToB2C',
                    }}
                    className="text-base leading-tight font-bold underline reset-button cursor-pointer flex items-center relative px-4 focus:text-blue hover:text-blue text-gray-darker"
                    url={backToB2CWebsiteLink}
                  >
                    {backToB2CWebsiteLabel}
                  </TrackClick>
                )}

                {!isMicePage && (
                  <Fragment>
                    <GlobalSearch
                      className="reset-button cursor-pointer flex items-center relative px-4 focus:text-blue hover:text-blue text-gray-darker"
                      isOpen={activeElement === ELEMENTS.SEARCH}
                      offsetTop={offsetTop}
                      toggleOpen={this.toggleElement(ELEMENTS.SEARCH)}
                    />
                    <NavigationMemberAccessButton
                      className={classnames(iconButtonClassNames, 'navigation-arrow', {
                        'text-blue navigation-arrow-active': activeElement === ELEMENTS.LOGIN,
                      })}
                      isActive={activeElement === ELEMENTS.LOGIN}
                      isAuthenticated={isLoggedIn}
                      isQuickLoggedIn={isQuickLoggedIn}
                      onClick={this.toggleElement(ELEMENTS.LOGIN)}
                      title={this.props.cmsMemberSpace?.title}
                    />
                    <NavigationLargeElement
                      className="rounded-small shadow-md"
                      onClose={this.toggleElement(ELEMENTS.LOGIN)}
                      style={{
                        width: '40rem',
                        ...(isLoggedIn || isQuickLoggedIn
                          ? { width: '18.125rem', padding: '0' }
                          : {}),
                      }}
                    >
                      {activeElement === ELEMENTS.LOGIN ? (
                        <NavigationMemberAccess
                          cmsAccount={this.props.cmsAccount}
                          cmsMemberSpace={this.props.cmsMemberSpace}
                          userInfo={userInfo}
                        />
                      ) : null}
                    </NavigationLargeElement>
                  </Fragment>
                )}

                {Object.keys(language?.locales || {}).length > 1 && (
                  <Fragment>
                    <button
                      aria-expanded={activeElement === ELEMENTS.LANGUAGES}
                      className={classnames(
                        iconButtonClassNames,
                        'text-center text-sm uppercase navigation-arrow',
                        {
                          'text-blue navigation-arrow-active': activeElement === ELEMENTS.LANGUAGES,
                        },
                      )}
                      data-testid="NavigationLarge-language-button"
                      onClick={this.toggleElement(ELEMENTS.LANGUAGES)}
                      style={{ minWidth: '3.5rem' }}
                      title="languages"
                      type="button"
                    >
                      <span
                        className={classnames('toggler', {
                          'toggler-active': activeElement === ELEMENTS.LANGUAGES,
                        })}
                      >
                        {language.locales[language.currentLocale].label}
                      </span>
                    </button>
                    <NavigationLargeElement
                      className="px-1 py-0"
                      onClose={this.toggleElement(ELEMENTS.LANGUAGES)}
                    >
                      {activeElement === ELEMENTS.LANGUAGES && (
                        <Languages
                          {...language}
                          currentLocale={language.currentLocale}
                          getTrackingData={getNavigationLanguagesButtonTrackingData}
                          isActive={activeElement === ELEMENTS.LANGUAGES}
                          locales={language.locales}
                          theme="column"
                        />
                      )}
                    </NavigationLargeElement>
                  </Fragment>
                )}
              </div>
            </div>
          </BodyLocker>
        </FocusOn>
      </nav>
    );
  }
}
